const audioCodecPrefixes = [
	'mp4a',
	'ac-3',
	'ec-3',
	'opus',
	'vorbis',
	'flac',
	'alac',
]
const videoCodecPrefixes = [
	'av01',
	'avc1',
	'avc2',
	'avc3',
	'avc4',
	'dvh1',
	'dvhe',
	'hev1',
	'hvc1',
	'theora',
	'vp8',
	'vp09',
]

function codecList(line: string): string[] | undefined {
	const match = line.match(/(?:^|,)\s*CODECS="([^"]+)"/i)
	return match?.[1]
		.split(',')
		.map((codec) => codec.trim().toLowerCase())
		.filter(Boolean)
}

export function hlsManifestHasAudio(manifest: string): boolean | undefined {
	const lines = manifest
		.split(/\r?\n/)
		.map((line) => line.trim())
		.filter(Boolean)

	if (
		lines.some(
			(line) =>
				line.startsWith('#EXT-X-MEDIA:') &&
				/(?:^|,)\s*TYPE=AUDIO(?:,|$)/i.test(
					line.slice('#EXT-X-MEDIA:'.length)
				)
		)
	)
		return true

	const variants = lines.filter((line) =>
		line.startsWith('#EXT-X-STREAM-INF:')
	)
	if (variants.length === 0) return undefined
	if (variants.some((line) => /(?:^|,)\s*AUDIO="[^"]+"/i.test(line)))
		return true

	const codecs = variants.map(codecList)
	if (codecs.some((value) => value === undefined)) return undefined

	if (
		codecs.some((variantCodecs) =>
			variantCodecs?.some((codec) =>
				audioCodecPrefixes.some(
					(prefix) =>
						codec === prefix || codec.startsWith(`${prefix}.`)
				)
			)
		)
	)
		return true

	const videoOnly = codecs.every((variantCodecs) =>
		variantCodecs?.every((codec) =>
			videoCodecPrefixes.some(
				(prefix) => codec === prefix || codec.startsWith(`${prefix}.`)
			)
		)
	)
	return videoOnly ? false : undefined
}

const audioAvailability = new Map<string, boolean>()

export async function hlsSourceHasAudio(
	source: string,
	signal?: AbortSignal
): Promise<boolean | undefined> {
	const sourceUrl = new URL(source, window.location.href).href
	const cached = audioAvailability.get(sourceUrl)
	if (cached !== undefined) return cached

	try {
		const response = await fetch(sourceUrl, { signal })
		if (!response.ok) return undefined
		const result = hlsManifestHasAudio(await response.text())
		if (result !== undefined) audioAvailability.set(sourceUrl, result)
		return result
	} catch (error) {
		if (error instanceof DOMException && error.name === 'AbortError') return undefined
		return undefined
	}
}
